import { Workspace } from "types/workspace";

export interface WorkspaceNode {
  workspace: Workspace;
  /** 0 for a workspace whose parent is not in the list (a top-level one, or the top of a delegated admin's subtree). */
  level: number;
  childCount: number;
}

/**
 * The list in tree order -- each workspace followed by its sub-workspaces -- with each one's level
 * inside this list. Siblings keep the order they came in. A parent that is not in the list (archived
 * and hidden, or outside the caller's subtree) makes its children start a new top.
 */
export const toTree = (workspaces: Workspace[]): WorkspaceNode[] => {
  const ids = new Set(workspaces.map((w) => w.id));
  const children = new Map<string, Workspace[]>();
  const roots: Workspace[] = [];
  workspaces.forEach((w) => {
    const parent = w.parentId && w.parentId !== w.id && ids.has(w.parentId) ? w.parentId : null;
    if (parent) {
      children.set(parent, [...(children.get(parent) ?? []), w]);
    } else {
      roots.push(w);
    }
  });

  const out: WorkspaceNode[] = [];
  const placed = new Set<string>();
  const place = (w: Workspace, level: number) => {
    if (placed.has(w.id)) return;
    placed.add(w.id);
    const kids = children.get(w.id) ?? [];
    out.push({ workspace: w, level, childCount: kids.length });
    kids.forEach((k) => place(k, level + 1));
  };
  roots.forEach((r) => place(r, 0));
  // Anything a loop in bad data kept from being reached still shows.
  workspaces.forEach((w) => place(w, 0));
  return out;
};

/** The names from the top of the list down to this workspace, e.g. ["Sales", "EMEA"]. */
export const pathOf = (workspaces: Workspace[], id: string): Workspace[] => {
  const byId = new Map(workspaces.map((w) => [w.id, w]));
  const path: Workspace[] = [];
  const seen = new Set<string>();
  let current = byId.get(id);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return path;
};

/** The workspace and every workspace below it, by id. */
export const subtreeIds = (workspaces: Workspace[], id: string): Set<string> => {
  const out = new Set<string>([id]);
  const queue = [id];
  while (queue.length) {
    const parent = queue.shift();
    workspaces
      .filter((w) => w.parentId === parent && !out.has(w.id))
      .forEach((w) => {
        out.add(w.id);
        queue.push(w.id);
      });
  }
  return out;
};

/** Levels below the workspace: 0 when it has no sub-workspaces. */
export const heightOf = (workspaces: Workspace[], id: string): number => {
  const kids = workspaces.filter((w) => w.parentId === id && w.id !== id);
  return kids.length ? 1 + Math.max(...kids.map((k) => heightOf(workspaces, k.id))) : 0;
};
